import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchDeliveryQuote, resetDeliveryFee } from "./deliveryFeeRedux";
import { toQuoteItems, quoteItemsKey } from "../api/deliveryQuote";

/**
 * useDeliveryFee — the single source of truth for the (server-authoritative)
 * delivery fee on every customer checkout screen.
 *
 * Wraps the deliveryFee slice + the signed `POST /api/quote` endpoint so each
 * PlaceOrder screen stops re-deriving `serverShippingFee` from the slice. The
 * fee, distance and zone are always whatever the SERVER returned; the client
 * never computes them. The returned `quoteToken` should be forwarded on
 * order-create as `clientQuoteToken` so the server can verify-and-honour the
 * exact fee the user was shown.
 *
 * @param {object}   params
 * @param {string}   params.vendorId      vendor/entity id (or owner id)
 * @param {string}   [params.outletId]    outlet id, when the order is for an
 *                                        outlet — the quote is then measured
 *                                        from the OUTLET's coords and radius
 * @param {number}   params.customerLat   drop-off latitude
 * @param {number}   params.customerLng   drop-off longitude
 * @param {Array}    [params.items]       order items (reserved, forwarded to /api/quote)
 * @param {string}   [params.mode]        'third_party' | 'vendor_own'
 * @param {string}   [params.paymentMethod] the checkout's payment method, so the
 *                                        server's total carries the processing
 *                                        fee order create adds for it
 * @param {boolean}  [params.enabled]     gate auto-fetch (default true)
 * @param {boolean}  [params.resetOnVendorChange] reset stale fee when the vendor changes (default false)
 *
 * @returns {{
 *   fee: number,
 *   distanceKm: number,
 *   zone: (string|null),
 *   status: ('idle'|'loading'|'succeeded'|'failed'),
 *   quoteToken: (string|null),
 *   isReady: boolean,
 *   isLoading: boolean,
 *   isFailed: boolean,
 *   breakdown: (object|null),
 *   refresh: () => void,
 * }}
 */
export default function useDeliveryFee({
  vendorId,
  outletId,
  customerLat,
  customerLng,
  items,
  mode,
  paymentMethod,
  enabled = true,
  resetOnVendorChange = false,
} = {}) {
  const dispatch = useDispatch();
  const {
    deliveryFee, distanceKm, zone, status, quoteToken, breakdown, error,
    fulfillment, freightTermsAccepted, offerWithdrawn, offer,
    itemsSubtotal, total, minimumOrder, paymentProcessing,
  } =
    useSelector((state) => state.deliveryFee);

  const itemsKey = quoteItemsKey(items);
  const quoteItems = useMemo(
    () => toQuoteItems(items),
    // The fingerprint changes only when an id/quantity changes; cart selectors
    // often return a new array on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemsKey]
  );

  // NB: Number.isFinite (not truthiness) — a drop-off exactly on the equator
  // (lat 0) is a real Ugandan coordinate, not a missing one.
  const hasCoords =
    Number.isFinite(Number(customerLat)) && Number.isFinite(Number(customerLng));
  const canFetch = Boolean(enabled && vendorId && hasCoords);

  const refresh = useCallback(() => {
    if (resetOnVendorChange) {
      // Clear any stale fee from a previous order before fetching fresh.
      dispatch(resetDeliveryFee());
    }
    if (!canFetch) return;
    dispatch(
      fetchDeliveryQuote({ vendorId, outletId, customerLat, customerLng, items: quoteItems, mode, paymentMethod })
    );
  }, [dispatch, canFetch, vendorId, outletId, customerLat, customerLng, mode, paymentMethod, resetOnVendorChange, quoteItems]);

  // Auto-fetch whenever the vendor or drop-off changes (mirrors the old per-screen
  // useEffect that dispatched fetchDeliveryFee on shippingAddress.latLng change).
  useEffect(() => {
    refresh();
  }, [refresh]);

  // The server withdrew the free-delivery offer this quote carried: quote
  // again so the screen shows, and the next order sends, the real fee.
  useEffect(() => {
    if (offerWithdrawn) refresh();
  }, [offerWithdrawn, refresh]);

  const isReady = status === "succeeded";

  return {
    // Server fee — only meaningful once the quote succeeded. Screens keep showing
    // the subtotal / "Calculating…" until then (display-only on the client).
    fee: isReady ? Number(deliveryFee) || 0 : 0,
    distanceKm,
    zone,
    status,
    quoteToken,
    isReady,
    isLoading: status === "loading",
    isFailed: status === "failed",
    // The quote can never even be REQUESTED: the shipping address has no
    // coordinates (or no vendor id). Without surfacing this, screens sit on
    // "Calculating…" forever (status stays 'idle') with the pay button dead —
    // the July-2026 stuck-checkout bug. Screens must tell the customer to
    // re-pick their delivery location instead.
    isBlocked: !canFetch,
    // Human-readable reason when isFailed (server 401/422/429/500 message).
    error: error || null,
    breakdown,
    // The free-delivery offer in this quote ({ kind, originalFee,
    // waivedAmount }), so the fee row can say why the fee is lower.
    offer: isReady ? offer : null,
    // The server's goods subtotal, total (goods plus this fee) and minimum
    // order verdict for the quoted lines; null until the quote is ready.
    itemsSubtotal: isReady ? itemsSubtotal : null,
    total: isReady ? total : null,
    minimumOrder: isReady ? minimumOrder : null,
    paymentProcessing: isReady ? paymentProcessing : null,
    // The server priced the quoted lines, so its minimum-order verdict stands
    // (utils/minimumOrder cartMinimumVerdict reads this object as it is).
    goodsPriced: isReady && itemsSubtotal !== null && itemsSubtotal !== undefined,
    fulfillment,
    freightTermsAccepted,
    freightConsentRequired: fulfillment?.required === true && freightTermsAccepted !== true,
    refresh,
  };
}
